import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, PlusCircle, Trash2, User } from 'lucide-react';
import Modal from '../components/Modal';


const AgendaScreen = ({ agendaEvents: events = [], currentUser, onAddEvent, onDeleteEvent }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newEvent, setNewEvent] = useState({ time: '09:00', title: '', description: '' });

    // --- HELPER: Converte qualquer formato de data para Objeto Date JS ---
    const getSafeDate = (dateInput) => {
        if (!dateInput) return null;
        if (typeof dateInput.toDate === 'function') return dateInput.toDate();
        if (dateInput.seconds !== undefined && dateInput.nanoseconds !== undefined) {
            return new Date(dateInput.seconds * 1000);
        }
        if (dateInput instanceof Date) return dateInput;
        if (typeof dateInput === 'string' || typeof dateInput === 'number') return new Date(dateInput);
        return null;
    };

    // --- HELPER: Comparação segura de dias ---
    const isSameDay = (date1, date2) => {
        if (!date1 || !date2) return false;
        return date1.toLocaleDateString() === date2.toLocaleDateString();
    };

    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startDay = startOfMonth.getDay();
    const totalDays = endOfMonth.getDate();

    const companyEvents = useMemo(() => {
        return events; 
    }, [events]);

    const daysInMonth = useMemo(() => {
        const days = [];
        for (let i = 0; i < startDay; i++) {
            days.push(null);
        }
        for (let i = 1; i <= totalDays; i++) {
            days.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), i));
        }
        return days;
    }, [currentDate, startDay, totalDays]);

    const handleDateClick = (date) => {
        if (!date) return;
        setSelectedDate(date);
        setIsModalOpen(true);
    };

    const handleAddEvent = (e) => {
        e.preventDefault();
        if (newEvent.title.trim() && selectedDate && newEvent.time) {
            
            const [hours, minutes] = newEvent.time.split(':');
            const combinedDate = new Date(
                selectedDate.getFullYear(), 
                selectedDate.getMonth(), 
                selectedDate.getDate(), 
                hours, 
                minutes
            );

            const now = new Date();
            if (combinedDate < now) {
                alert("Não é possível criar agendamentos para datas ou horários passados.");
                return;
            }
            
            onAddEvent({
                title: newEvent.title,
                description: newEvent.description,
                time: newEvent.time,
                date: combinedDate,
            });
            
            setNewEvent({ time: '09:00', title: '', description: '' });
            setIsModalOpen(false); 
        }
    };

    const handleDeleteEvent = (event) => {
        const isOwner = event.authorId === currentUser.uid;
        const isAdmin = currentUser.role === 'admin' || currentUser.is_supervisor;

        if (isOwner || isAdmin) {
            if (window.confirm("Deseja realmente excluir este agendamento?")) {
                onDeleteEvent(event.id);
            }
        } else {
            alert("Você só pode excluir agendamentos criados por você.");
        }
    };

    const changeMonth = (offset) => {
        setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    };

    const eventsForSelectedDate = useMemo(() => {
        if (!selectedDate) return [];
        
        return companyEvents
            .filter(event => {
                const evtDate = getSafeDate(event.date);
                return isSameDay(evtDate, selectedDate);
            })
            .sort((a, b) => a.time.localeCompare(b.time));
    }, [selectedDate, companyEvents]);


    return (
        <>
            <div className="p-8 h-full overflow-y-auto bg-gray-50">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-gray-800">Agenda da Equipe</h1>
                    <div className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm border">
                        Visualizando agenda compartilhada
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md">
                    <div className="flex items-center justify-between mb-4">
                        <button onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-gray-100"><ChevronLeft /></button>
                        <h2 className="text-xl font-bold text-gray-700">
                            {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </h2>
                        <button onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-gray-100"><ChevronRight /></button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center">
                        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                            <div key={day} className="font-bold text-gray-500 text-sm py-2">{day}</div>
                        ))}
                        {daysInMonth.map((day, index) => {
                            
                            const dayEventsCount = day 
                                ? companyEvents.filter(e => isSameDay(getSafeDate(e.date), day)).length 
                                : 0;

                            const isPastDay = day && day < new Date().setHours(0,0,0,0);

                            return (
                                <div
                                    key={index}
                                    onClick={() => handleDateClick(day)}
                                    className={`p-1 h-20 border rounded-md flex flex-col items-center justify-start cursor-pointer transition-colors 
                                        ${!day ? 'bg-gray-50 border-transparent cursor-default' : ''}
                                        ${day && !isPastDay ? 'hover:bg-blue-50 bg-white' : ''}
                                        ${isPastDay ? 'bg-gray-100 opacity-70' : ''}
                                    `}
                                >
                                    <span className={`font-semibold mb-1 ${day ? (isPastDay ? 'text-gray-400' : 'text-gray-700') : 'text-gray-400'}`}>
                                        {day?.getDate()}
                                    </span>
                                    
                                    {dayEventsCount > 0 && (
                                        <div className="bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-sm animate-in zoom-in duration-200">
                                            {dayEventsCount}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={`Eventos de ${selectedDate?.toLocaleDateString()}`}
                size="max-w-2xl"
            >
                {/* CORREÇÃO: Wrapper com scroll para evitar que o modal ultrapasse a altura da tela */}
                <div className="max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                    
                    <div className="mb-6 pb-6 border-b">
                        <h3 className="text-lg font-semibold mb-3">Adicionar Novo Evento</h3>
                        <form onSubmit={handleAddEvent} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <input type="time" value={newEvent.time} onChange={e => setNewEvent({ ...newEvent, time: e.target.value })} className="md:col-span-1 px-3 py-2 border rounded-md" required />
                            <input type="text" placeholder="Título do evento" value={newEvent.title} onChange={e => setNewEvent({ ...newEvent, title: e.target.value })} className="md:col-span-2 px-3 py-2 border rounded-md" required />
                            <textarea placeholder="Descrição (opcional)..." value={newEvent.description} onChange={e => setNewEvent({ ...newEvent, description: e.target.value })} className="md:col-span-3 px-3 py-2 border rounded-md" rows="2"></textarea>
                            <button type="submit" className="md:col-span-3 w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 flex items-center justify-center font-medium">
                                <PlusCircle className="w-5 h-5 mr-2" /> Salvar na Agenda Compartilhada
                            </button>
                        </form>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold mb-3">Eventos Agendados</h3>
                        <div className="space-y-3">
                            {eventsForSelectedDate.length > 0 ? (
                                eventsForSelectedDate.map(event => {
                                    const isOwner = event.authorId === currentUser.uid;
                                    const isAdmin = currentUser.role === 'admin' || currentUser.is_supervisor;
                                    const canDelete = isOwner || isAdmin;

                                    return (
                                        <div key={event.id} className={`p-4 rounded-lg flex justify-between items-start border transition-shadow ${isOwner ? 'bg-blue-50 border-blue-100' : 'bg-gray-50 border-gray-200'}`}>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-bold text-blue-600 text-lg">{event.time}</span>
                                                    <span className="font-semibold text-gray-800 text-lg">{event.title}</span>
                                                </div>
                                                
                                                <div className="flex items-center text-xs text-gray-500 mb-1">
                                                    <User className="w-3 h-3 mr-1" />
                                                    <span>Agendado por: <strong>{event.authorName || 'Desconhecido'}</strong></span>
                                                </div>

                                                {event.description && <p className="text-sm text-gray-600 ml-1">{event.description}</p>}
                                            </div>
                                            
                                            {canDelete && (
                                                <button onClick={() => handleDeleteEvent(event)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Excluir">
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                    <p className="text-gray-500 italic">Nenhum evento para esta data.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default AgendaScreen;